<template>
  <div class="theme-stacked-bar-chart">
    <Bar
      :chart-data="chartData"
      :chart-options="chartOptions"
    />
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs/legacy';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default {
  name: 'ThemeStackedBarChart',
  components: { Bar },
  
  props: {
    categories: {
      type: Array,
      required: true
    },
    datasets: {
      type: Array,
      required: true
    },
    yAxisLabel: {
      type: String,
      default: ''
    },
    yAxisMaxValue: {
      type: Number,
      default: null
    },
    formatValue: {
      type: Function,
      default: (value) => {
        // Format as K (thousands)
        if (value >= 1000) {
          return (value / 1000).toFixed(1) + 'K';
        }
        return value.toLocaleString();
      }
    }
  },

  data() {
    return {
      isDarkMode: document.documentElement.classList.contains('dark'),
      themeObserver: null
    };
  },

  mounted() {
    // Watch for theme changes
    this.themeObserver = new MutationObserver(() => {
      this.isDarkMode = document.documentElement.classList.contains('dark');
    });
    
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  },

  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },

  computed: {
    chartData() {
      if (!this.datasets || !Array.isArray(this.datasets) || !this.categories || !Array.isArray(this.categories)) {
        return {
          labels: [],
          datasets: []
        };
      }

      return {
        labels: this.categories,
        datasets: this.datasets.map(dataset => ({
          label: dataset.label,
          data: dataset.data,
          backgroundColor: dataset.color,
          borderWidth: 0,
          barThickness: 'flex',
          maxBarThickness: 40
        }))
      };
    },

    chartOptions() {
      const textColor = this.isDarkMode ? '#94a3b8' : '#374151';
      const gridColor = this.isDarkMode ? '#1e293b' : '#e5e7eb';
      const tooltipBg = this.isDarkMode ? '#1e293b' : '#ffffff';
      const tooltipBorder = this.isDarkMode ? '#334155' : '#e5e7eb';

      return {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              color: textColor,
              padding: 15,
              usePointStyle: true,
              pointStyle: 'circle'
            }
          },
          tooltip: {
            backgroundColor: tooltipBg,
            titleColor: textColor,
            bodyColor: textColor,
            borderColor: tooltipBorder,
            borderWidth: 1,
            padding: 12,
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || '';
                const value = context.parsed.x;
                return `${label}: ${this.formatValue(value)}`;
              }
            }
          }
        },
        scales: {
          x: {
            stacked: true,
            grid: {
              color: gridColor,
              drawBorder: false
            },
            ticks: {
              color: textColor,
              callback: (value) => this.formatValue(value)
            },
            title: {
              display: !!this.yAxisLabel,
              text: this.yAxisLabel,
              color: textColor
            },
            max: this.yAxisMaxValue
          },
          y: {
            stacked: true,
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              color: textColor
            }
          }
        }
      };
    }
  }
};
</script>
